// frontend/app/components/lessonPlans/present/PresentBlock.js
//
// Dispatches one authored block to its presentation renderer — the same job
// LessonBlockRenderer does for the workspace, and the same rule: a new block
// type means a case here and a component beside it, never a per-lesson branch.
//
// The two lists differ in exactly the ways the teaching situation demands.
// teacher_tip and mastery_map are absent (presentRules routes the first into
// the notes panel and drops the second, which is one student's data and there
// are no students here). Six separate free-text types collapse onto one
// renderer, because with nobody typing, a prediction and a reflection are both
// just a question put to the room.

import React from "react";

import { revealFor } from "./presentRules";
import { PresentLane, RevealButton } from "./PresentSurface";
import {
  PresentChoiceBlock,
  PresentDiscussBlock,
  PresentSequenceBlock,
} from "./PresentCheckBlocks";
import {
  PresentAiPromptBlock,
  PresentBulletListBlock,
  PresentCalloutBlock,
  PresentRichTextBlock,
} from "./PresentTextBlocks";
import PresentAssetBlock from "./PresentAssetBlock";
import { getPhaseMeta } from "../lessonPlanTheme";

/** The kicker above a free-text prompt, per type. */
const DISCUSS_EYEBROW = {
  prediction: "Predict first",
  explanation: "Explain your thinking",
  exploration_probe: "Quick probe",
  reflection: "Look back",
  next_challenge: "Next up",
  short_answer: "Discuss",
};

const DISCUSS_PHASE = {
  prediction: "wonder",
  explanation: "explain",
  exploration_probe: "explore",
  reflection: "reflect",
  next_challenge: "grow",
};

const DISCUSS_TYPES = Object.keys(DISCUSS_EYEBROW);

const PresentBlock = ({
  block,
  colors,
  type,
  isDarkMode,
  revealed = false,
  onReveal,
  onOpenAsset,
  // Pressing a question on the slide opens the Saras rail already asking it.
  onAskQuestion,
  // Pressing an asset's summary opens the same rail showing authored text —
  // no ask behind it. Only the asset stage uses it; see PresentAssetBlock for
  // why its absence removes the control rather than degrading it.
  onShowSummary,
  // Only the asset stage uses these — it plays the scene and the video itself,
  // because the workspace that normally owns those players is not mounted here.
  authToken,
  localLanguage = "en",
  languageId = 1,
  t,
}) => {
  const translate = t || ((value) => value);
  if (!block || typeof block !== "object") return null;

  const reveal = revealFor(block);
  const shared = { block, colors, type, isDarkMode, onAskQuestion, t };

  const body = (() => {
    switch (block.type) {
      case "rich_text":
        return <PresentRichTextBlock {...shared} />;
      case "bullet_list":
        return <PresentBulletListBlock {...shared} />;
      case "callout":
        return <PresentCalloutBlock {...shared} isDarkMode={isDarkMode} />;
      case "ai_prompt":
        return <PresentAiPromptBlock {...shared} />;

      case "saras_pwo":
      case "saras_video":
      case "saras_topic_quiz":
        return (
          <PresentAssetBlock
            {...shared}
            onOpenAsset={onOpenAsset}
            onShowSummary={onShowSummary}
            authToken={authToken}
            localLanguage={localLanguage}
            languageId={languageId}
          />
        );

      case "multiple_choice":
      case "multi_select":
        return <PresentChoiceBlock {...shared} reveal={reveal} revealed={revealed} />;

      case "ordering":
        return <PresentSequenceBlock {...shared} reveal={reveal} revealed={revealed} />;

      default:
        if (DISCUSS_TYPES.includes(block.type)) {
          const phase = DISCUSS_PHASE[block.type];
          return (
            <PresentDiscussBlock
              {...shared}
              reveal={reveal}
              revealed={revealed}
              accent={phase ? getPhaseMeta(phase).accent : colors.accentPrimary}
              eyebrow={DISCUSS_EYEBROW[block.type]}
            />
          );
        }
        // Unreachable in practice: presentRules.PRESENTABLE_BLOCK_TYPES has
        // already filtered anything this switch cannot draw, so that a step
        // left with no drawable blocks can say so rather than showing a title
        // over an empty wall. This is the second half of that guarantee.
        return null;
    }
  })();

  if (!body) return null;

  const isAsset = block.type.startsWith("saras_");

  return (
    <PresentLane wide={isAsset}>
      {body}
      {/* Rendered only where revealFor found something. A block with nothing to
          reveal never grows this control. */}
      {reveal && !revealed && onReveal ? (
        <RevealButton
          type={type}
          colors={colors}
          label={translate("Reveal")}
          onPress={onReveal}
        />
      ) : null}
    </PresentLane>
  );
};

export default PresentBlock;
